import * as React from 'react';
import { useState, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import MuiCard from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMediaQuery } from '@mui/material';

import { styled } from '@mui/material/styles';
import { usePayment } from './PaymentContext'; // Import the context

import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import SimCardRoundedIcon from '@mui/icons-material/SimCardRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import visaLogo from '../visa.png'; // Adjust the path as needed
import mastercardLogo from '../mastercard.png'; // Adjust the path as needed
import americanLogo from '../american-express.png'; // Adjust the path as needed
import dinersLogo from '../diners-club.png'; // Adjust the path as needed

const Card = styled(MuiCard)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.divider,
  width: '100%',
  '&:hover': {
    background:
      'linear-gradient(to bottom right, hsla(210, 100%, 97%, 0.5) 25%, hsla(210, 100%, 90%, 0.3) 100%)',
    borderColor: 'primary.light',
    boxShadow: '0px 2px 8px hsla(0, 0%, 0%, 0.1)',
    ...theme.applyStyles('dark', {
      background:
        'linear-gradient(to right bottom, hsla(210, 100%, 12%, 0.2) 25%, hsla(210, 100%, 16%, 0.2) 100%)',
      borderColor: 'primary.dark',
      boxShadow: '0px 1px 8px hsla(210, 100%, 25%, 0.5) ',
    }),
  },
  [theme.breakpoints.up('md')]: {
    flexGrow: 1,
    maxWidth: `calc(50% - ${theme.spacing(1)})`,
  },
  variants: [
    {
      props: ({ selected }) => selected,
      style: {
        borderColor: theme.palette.primary.light,
        ...theme.applyStyles('dark', {
          borderColor: theme.palette.primary.dark,
        }),
      },
    },
  ],
}));

const PaymentContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  width: '100%',
  height: 375,
  padding: theme.spacing(3),
  borderRadius: `calc(${theme.shape.borderRadius}px + 4px)`,
  border: '1px solid ',
  borderColor: theme.palette.divider,
  background:
    'linear-gradient(to bottom right, hsla(220, 35%, 97%, 0.3) 25%, hsla(220, 20%, 88%, 0.3) 100%)',
  boxShadow: '0px 4px 8px hsla(210, 0%, 0%, 0.05)',
  [theme.breakpoints.up('xs')]: {
    height: 300,
  },
  [theme.breakpoints.up('sm')]: {
    height: 350,
  },
  ...theme.applyStyles('dark', {
    background:
      'linear-gradient(to right bottom, hsla(220, 30%, 6%, 0.2) 25%, hsla(220, 20%, 25%, 0.2) 100%)',
    boxShadow: '0px 4px 8px hsl(220, 35%, 0%)',
  }),
}));

const FormGrid = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const PaymentForm = forwardRef((props, ref) => {
  const [paymentType, setPaymentType] = useState('creditCard');
  const [errors, setErrors] = useState({ name: '' });
  const isMobile = useMediaQuery('(max-width:600px)');

  const {
    cardNumber,
    setCardNumber,
    cvv,
    setCvv,
    expirationDate,
    setExpirationDate,
    cardName,
    setCardName,
    email,
    setEmail
  } = usePayment(); // Use the context

  const handlePaymentTypeChange = (event) => {
    setPaymentType(event.target.value);
  };

  const handleCardNumberChange = (event) => {
    const value = event.target.value.replace(/\D/g, '');
    const formattedValue = value.replace(/(\d{4})(?=\d)/g, '$1 ');
    if (value.length <= 16) {
      setCardNumber(formattedValue);
    }
  };

  const handleCvvChange = (event) => {
    const value = event.target.value.replace(/\D/g, '');
    if (value.length <= 3) {
      setCvv(value);
    }
  };

  const handleExpirationDateChange = (event) => {
    const value = event.target.value.replace(/\D/g, '');
    const formattedValue = value.replace(/(\d{2})(?=\d{2})/, '$1/');
    if (value.length <= 4) {
      setExpirationDate(formattedValue);
    }
  };

  const validateCardNumber = (number) => {
    const cleanedNumber = number.replace(/\D/g, ''); // Remove non-digit characters
    
    // Check if the card number length is valid for any known card type
    const cardType = getCardType(cleanedNumber);
    const cardInfo = cardTypes.find(card => card.name === cardType);
  
    if (cardInfo) {
      const isValidLength = cardInfo.lengths.includes(cleanedNumber.length);
      if (!isValidLength) {
        return false; // Invalid length
      }
    } else {
      return false; // Unknown card type
    }
  
    // Validate the card number using the Luhn algorithm
    return luhnCheck(cleanedNumber);
  };
  
  // Luhn Algorithm implementation
  const luhnCheck = (number) => {
    let sum = 0;
    let alternate = false;
  
    for (let i = number.length - 1; i >= 0; i--) {
      let n = parseInt(number.charAt(i), 10);
  
      if (alternate) {
        n *= 2;
        if (n > 9) n -= 9; // Subtract 9 from numbers over 9
      }
  
      sum += n;
      alternate = !alternate;
    }
  
    return sum % 10 === 0; // Valid if sum modulo 10 is 0
  };

  const validateExpirationDate = (date) => {
    const regex = /^(0[1-9]|1[0-2])\/?([0-9]{2})$/; // MM/YY format
    const matches = date.match(regex);
    
    if (!matches) return false; // Not in valid format
  
    const month = parseInt(matches[1], 10);
    const year = parseInt(matches[2], 10) + 2000; // Convert YY to YYYY
  
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // Months are 0-based
  
    // Check if the date is in the future
    return year > currentYear || (year === currentYear && month >= currentMonth);
  };

  const cardTypes = [
    {
      name: 'Visa',
      prefixes: ['4'],
      lengths: [13, 16],
    },
    {
      name: 'MasterCard',
      prefixes: ['51', '52', '53', '54', '55'],
      lengths: [16],
    },
    {
      name: 'American Express',
      prefixes: ['34', '37'],
      lengths: [15],
    },
    {
      name: 'Discover',
      prefixes: ['6011', '65', '644', '645', '646', '647', '648', '649'],
      lengths: [16],
    },
    {
      name: 'Diners Club',
      prefixes: ['36', '38', '39'],
      lengths: [14],
    },
    {
      name: 'JCB',
      prefixes: ['3528', '3589'],
      lengths: [16],
    },
    {
      name: 'UnionPay',
      prefixes: ['62'],
      lengths: [16, 17, 18, 19],
    },
  ];

  const getCardType = (number) => {
    // Remove all non-digit characters
    const cleanedNumber = number.replace(/\D/g, '');
    
    for (const card of cardTypes) {
      const { prefixes, lengths } = card;
  
      const startsWithPrefix = prefixes.some((prefix) => cleanedNumber.startsWith(prefix));
      const hasValidLength = lengths.includes(cleanedNumber.length);
  
      if (startsWithPrefix && hasValidLength) {
        return card.name; // Return the card name if a match is found
      }
    }
  
    return 'Unknown'; // Return 'Unknown' if no match found
  };

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail); // This updates the context
  };

  const validateEmail = (email) => {
    // Simple regex for email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateName = (name) => {
    // Check for at least two words and only letters (including spaces between words)
    const regex = /^[A-Za-z]+(?: [A-Za-z]+){1,}$/;
  
    return regex.test(name.trim()); // Test the trimmed name against the regex
  };

  const validate = (event) => {
    let hasErrors = false;
    const newErrors = { name: '', cardNumber: '', cvv: '', expirationDate: '', email: '' };
  
     // Name validation
    if (!cardName.trim()) {
      newErrors.name = 'Full name is required';
      hasErrors = true;
    } else if (!validateName(cardName)) {
      newErrors.name = 'Full name is required';
      hasErrors = true;
    }else {
      
    }
  
    // Card number validation
    if (!cardNumber.trim()) {
      newErrors.cardNumber = 'Card number is required';
      hasErrors = true;
    } else if (!validateCardNumber(cardNumber)) {
      newErrors.cardNumber = 'Card number not valid';
      hasErrors = true;
    }
  
    // CVV validation
    if (!cvv.trim() || cvv.length < 3) {
      newErrors.cvv = 'CVV is required ';
      hasErrors = true;
    }
  
    // Expiration date validation
    if (!expirationDate.trim() || !validateExpirationDate(expirationDate)) {
      newErrors.expirationDate = 'Invalid date';
      hasErrors = true;
    }

    // Email validation
  if (!email.trim()) {
    newErrors.email = 'Email is required';
    hasErrors = true;
  } else if (!validateEmail(email)) {
    newErrors.email = 'Invalid email address';
    hasErrors = true;
  }else {
    const firstName = cardName.split(' ')[0]; // Get the first name
    props.onSubmit({email: email, firstName: firstName})
  }
  
    setErrors(newErrors);
    return !hasErrors; // Return true if there are no errors
  };

  useImperativeHandle(ref, () => ({
    validate,
  }));

  return (
    <Stack spacing={{ xs: 3, sm: 6 }} useFlexGap>
      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          aria-label="Payment options"
          name="paymentType"
          value={paymentType}
          onChange={handlePaymentTypeChange}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          {/* Cards for payment type selection */}
        </RadioGroup>
      </FormControl>
      {paymentType === 'creditCard' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PaymentContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2">Credit card</Typography>
              <CreditCardRoundedIcon sx={{ color: 'text.secondary' }} />
            </Box>
            <SimCardRoundedIcon
              sx={{
                fontSize: { xs: 48, sm: 56 },
                transform: 'rotate(90deg)',
                color: 'text.secondary',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 2,
              }}
            >
              <FormGrid sx={{ flexGrow: 1 }}>
              {isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', }}>
                  <FormLabel sx={{ fontFamily: 'math', marginRight: '10%' }} htmlFor="card-number" required>
                  Card number
                  </FormLabel>

                  
                </Box>
              )}
              {!isMobile && (
                <FormLabel sx={{ fontFamily: 'math', marginRight: '10px' }} htmlFor="card-number" required>
                  Card number
                </FormLabel>
              )}
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  
                  <OutlinedInput
                    id="card-number"
                    autoComplete="card-number"
                    placeholder="0000 0000 0000 0000"
                    required
                    size="small"
                    value={cardNumber}
                    onChange={handleCardNumberChange}
                    endAdornment={!isMobile && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img
                          src={visaLogo}
                          alt="Visa"
                          style={{ width: '25px', height: 'auto', marginLeft: '5px' }}
                        />
                        <img
                          src={mastercardLogo}
                          alt="MasterCard"
                          style={{ width: '25px', height: 'auto', marginLeft: '5px' }}
                        />
                        <img
                          src={americanLogo}
                          alt="American Express"
                          style={{ width: '25px', height: 'auto', marginLeft: '5px' }}
                        />
                        <img
                          src={dinersLogo}
                          alt="Diners Club"
                          style={{ width: '25px', height: 'auto', marginLeft: '5px' }}
                        />
                      </Box>
                      )}
                      inputProps={{
                        style: { paddingRight: '50px' }
                    }}
                  />
                </Box>
                {errors.cardNumber && <Typography color="error">{errors.cardNumber}</Typography>}
              </FormGrid>
              <FormGrid sx={{ flexGrow: 1, maxWidth: '25%' }}>
                <FormLabel sx={{ fontFamily: 'math' }} htmlFor="card-expiration" required>
                {isMobile ? 'Exp' : 'Expiration date'}
                </FormLabel>
                <OutlinedInput
                  id="card-expiration"
                  autoComplete="card-expiration"
                  placeholder="MM/YY"
                  required
                  size="small"
                  value={expirationDate}
                  onChange={handleExpirationDateChange}
                />
                {errors.expirationDate && <Typography color="error">{errors.expirationDate}</Typography>}
              </FormGrid>
              <FormGrid sx={{ maxWidth: '15%' }}>
                <FormLabel sx={{ fontFamily: 'math' }} htmlFor="cvv" required>
                  CVV
                </FormLabel>
                <OutlinedInput
                  id="cvv"
                  autoComplete="CVV"
                  placeholder="123"
                  required
                  size="small"
                  value={cvv}
                  onChange={handleCvvChange}
                />
                {errors.cvv && <Typography color="error">{errors.cvv}</Typography>}
              </FormGrid>
            </Box>
              {/* <Box sx={{ display: 'flex', gap: 2 }}>
              

              
              </Box>
               */}
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormGrid sx={{ flexGrow: 1 }}>
                <FormLabel sx={{ fontFamily: 'math' }} htmlFor="card-name" required>
                  Cardholder name
                </FormLabel>
                <OutlinedInput
                  id="card-name"
                  autoComplete="card-name"
                  placeholder="Full name on card"
                  required
                  size="small"
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                />
                {errors.name && <Typography color="error">{errors.name}</Typography>}
              </FormGrid>
              
              <FormGrid sx={{ flexGrow: 1 }}>
                <FormLabel sx={{ fontFamily: 'math' }} htmlFor="card-email" required>
                  Email
                </FormLabel>
                <OutlinedInput
                  id="card-email"
                  autoComplete="card-email"
                  placeholder="example@gmail.com"
                  required
                  size="small"
                  value={email}
                  onChange={handleEmailChange}
                />
                {errors.email && <Typography color="error">{errors.email}</Typography>}
              </FormGrid>
            </Box>
          </PaymentContainer>
        </Box>
      )}
      {paymentType === 'bankTransfer' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning" icon={<WarningRoundedIcon />}>
            Your order will be processed once we receive the funds.
          </Alert>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            Bank account
          </Typography>
          <Typography variant="body1" gutterBottom>
            Please transfer the payment to the bank account details shown below.
          </Typography>
          {/* Bank account details */}
        </Box>
      )}
    </Stack>
  );
});

export default PaymentForm;